import React, { FunctionComponent, useContext } from "react";
import { Box, makeStyles, Theme, Typography, useTheme } from "@material-ui/core";
import { Dialog, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";
import { MenuCategoryDictionary } from "../../models/categories";
import { CircleColorIcon } from "../../assets/icons/circle-color-icon";
import { Cancel } from "../../assets/icons/cancel";
import { CONTENT_MAX_WIDTH } from "../../theme/theme";
import { ReferenceText } from "./components/reference-text";

interface INutrientModalStyleProps {
  color: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  bottomContainerWrapper: {
    backgroundColor: theme.palette.background.default,
  },
  bottomContainer: {
    padding: theme.spacing(10),
    paddingTop: theme.spacing(20),
  },
  cardBottomTitle: {
    fontSize: 14,
    fontWeight: 700,
  },
  cardBottomBody: {
    fontSize: 14,
    fontWeight: 300,
  },

  close: {
    position: "absolute",
    top: 0,
    left: 40,
    width: 24,
    height: 24,
    padding: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    cursor: "pointer",
    zIndex: 100,
    transition: "background-color 0.3s ease",
    backgroundColor: ({ color }: INutrientModalStyleProps) => color,
  },
  dialogPaper: {
    maxWidth: CONTENT_MAX_WIDTH,
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },
  dialogPaperRoot: {
    [theme.breakpoints.up("md")]: {
      minWidth: 750,
    },
  },
}));

interface IReferenceModalProps {
  open: boolean;
  onClose: () => void;
  references: string[];
}

export const ReferenceModal: FunctionComponent<IReferenceModalProps> = (props) => {
  const { activeCategory } = useContext(NutritionContext);
  const { color } = MenuCategoryDictionary[activeCategory];
  const theme = useTheme();
  const classes = useStyles({ color: color ? color : theme.palette.primary.main });
  const { t } = useTranslation();
  const { references, open, onClose } = props;

  return (
    <Dialog
      PaperProps={{
        className: classes.dialogPaper,
        classes: {
          root: classes.dialogPaperRoot,
        },
        style: {
          borderRadius: 40,
          maxHeight: "90vh",
        },
      }}
      open={open}
      onClose={onClose}
      fullWidth
    >
      <Box className={classes.close} onClick={onClose}>
        <Cancel width="24px" height="24px" style={{ cursor: "pointer" }} />
      </Box>

      <Grid container direction="column" alignItems="flex-start">
        <Grid item container className={classes.bottomContainerWrapper}>
          <Grid item container spacing={2} className={classes.bottomContainer}>
            <Grid item>
              <Typography className={classes.cardBottomTitle}>{t("nutrient_modal.references")}:</Typography>
            </Grid>

            {references.map((reference: string, idx: number) => (
              <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                spacing={1}
                key={`${reference.substring(0, 5)}-${idx}`}
              >
                <Grid item id={`${idx + 1}`}>
                  <Typography className={classes.cardBottomBody}>
                    <CircleColorIcon color={theme.palette.primary.main}>{`${idx + 1}`}</CircleColorIcon>
                  </Typography>
                </Grid>
                <Grid item xs>
                  <ReferenceText reference={reference} />
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};
